
import { useEffect, useRef, useState, useCallback } from 'react';
import Pusher from 'pusher-js';
import baseUrl, { broadcastingAuthUrl } from '../url';
import { getActiveRestaurantSystemId } from '@/lib/idProvider';

/**
 * Singleton Pusher Manager - Manages a single Pusher instance across the application
 */
class PusherManager {
    constructor() {
        this.pusher = null;
        this.channel = null;
        this.subscribers = new Set();
        this.connectionStatus = 'disconnected';
        this.error = null;
        this.eventHandlers = {};
        this.isInitialized = false;
    }

    async initialize() {
        if (this.isInitialized) {
            return this.pusher;
        }

        const token = localStorage.getItem('token');
        const restaurant_system_id = getActiveRestaurantSystemId() || localStorage.getItem('restaurant_system_id');
        
        if (!token || !restaurant_system_id) {
            throw new Error('Missing authentication credentials');
        }

        // Enable logging in development
        if (import.meta.env.DEV) {
            Pusher.logToConsole = true;
        }

        const pusherKey =
            import.meta.env.VITE_PUSHER_APP_KEY ||
            import.meta.env.PUSHER_APP_KEY ||
            import.meta.env.VITE_PUSHER_APP_SECRET ||
            import.meta.env.PUSHER_APP_SECRET ||
            import.meta.env.VITE_PUSHER_KEY;

        const pusherCluster =
            import.meta.env.VITE_PUSHER_APP_CLUSTER ||
            import.meta.env.PUSHER_APP_CLUSTER ||
            import.meta.env.VITE_PUSHER_CLUSTER;

        if (!pusherKey) {
            const missingKeyErr = 'Pusher Key is missing in .env (PUSHER_APP_KEY / PUSHER_APP_SECRET / VITE_PUSHER_APP_KEY)';
            console.error('❌', missingKeyErr);
            throw new Error(missingKeyErr);
        }

        const authEndpoint = broadcastingAuthUrl || `${new URL(baseUrl).origin}/broadcasting/auth`;

        try {
            this.pusher = new Pusher(pusherKey, {
                cluster: pusherCluster,
                authEndpoint,
                auth: {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json',
                    }
                }
            });

            // Connection events
            this.pusher.connection.bind('connected', () => {
                this.connectionStatus = 'connected';
                this.error = null;
                this.notifySubscribers();
            });

            this.pusher.connection.bind('connecting', () => {
                this.connectionStatus = 'connecting';
                this.notifySubscribers();
            });

            this.pusher.connection.bind('disconnected', () => {
                this.connectionStatus = 'disconnected';
                this.notifySubscribers();
            });

            this.pusher.connection.bind('error', (err) => {
                console.error('Pusher Error:', err);
                this.error = err.message || 'Connection error';
                this.connectionStatus = 'error';
                this.notifySubscribers();
            });

            // Subscribe to channel
            const channelName = `private-${restaurant_system_id}`;
            this.channel = this.pusher.subscribe(channelName);

            // Add channel event listeners
            this.channel.bind('pusher:subscription_succeeded', () => {
                // subscription succeeded
            });

            this.channel.bind('pusher:subscription_error', (error) => {
                console.error('❌ Channel subscription error:', error);
            });

            // Bind default event handlers
            this.addEventHandler('live-table-update', (data) => {
                // Check if this event has a specific event_type and dispatch it
                if (data && data.event_type) {
                    const handlers = this.eventHandlers[data.event_type];
                    if (handlers && handlers.size > 0) {
                        handlers.forEach((fn) => {
                            try {
                                fn(data);
                            } catch (err) {
                                console.error(`❌ Error in sub-handler for '${data.event_type}':`, err);
                            }
                        });
                    }
                }
                
                // Also notify general subscribers
                this.notifySubscribers('update', { type: 'live-table-update', data, timestamp: Date.now() });
            });

            this.addEventHandler('live-reservation-update', (data) => {
                // Check if this event has a specific event_type and dispatch it
                if (data && data.event_type) {
                    const handlers = this.eventHandlers[data.event_type];
                    if (handlers && handlers.size > 0) {
                        handlers.forEach((fn) => {
                            try {
                                fn(data);
                            } catch (err) {
                                console.error(`❌ Error in sub-handler for '${data.event_type}':`, err);
                            }
                        });
                    }
                }
                
                // Also notify general subscribers
                this.notifySubscribers('update', { type: 'live-reservation-update', data, timestamp: Date.now() });
            });

            this.addEventHandler('table-status-notification', (data) => {
                this.notifySubscribers('update', { type: 'table-status-notification', data, timestamp: Date.now() });
            });

            // Bind any pre-registered handlers to the newly subscribed channel
            if (this.eventHandlers) {
                Object.keys(this.eventHandlers).forEach((eventName) => {
                    if (!this.channelDispatchers?.[eventName]) {
                        const debugHandler = (data) => {
                            const handlers = this.eventHandlers[eventName];
                            if (handlers) {
                                handlers.forEach((fn) => {
                                    try {
                                        fn(data);
                                    } catch (err) {
                                        console.error(`❌ Error in handler for '${eventName}':`, err);
                                    }
                                });
                            }
                        };
                        this.channelDispatchers = this.channelDispatchers || {};
                        this.channelDispatchers[eventName] = debugHandler;
                        this.channel.bind(eventName, debugHandler);
                    }
                });
            }

            this.isInitialized = true;
            
            return this.pusher;
        } catch (err) {
            console.error('Failed to initialize Pusher:', err);
            this.error = 'Failed to initialize connection';
            this.connectionStatus = 'error';
            throw err;
        }
    }

    subscribe(callback) {
        this.subscribers.add(callback);
        // Immediately notify with current status
        callback('status', {
            connectionStatus: this.connectionStatus,
            error: this.error,
            isConnected: this.connectionStatus === 'connected'
        });

        return () => {
            this.subscribers.delete(callback);
        };
    }

    notifySubscribers(type = 'status', data = null) {
        const payload = data || {
            connectionStatus: this.connectionStatus,
            error: this.error,
            isConnected: this.connectionStatus === 'connected'
        };

        this.subscribers.forEach(callback => {
            try {
                callback(type, payload);
            } catch (err) {
                console.error('Error in subscriber callback:', err);
            }
        });
    }

    addEventHandler(eventName, handler) {
        if (!this.eventHandlers[eventName]) {
            this.eventHandlers[eventName] = new Set();
        }
        
        this.eventHandlers[eventName].add(handler);

        if (this.channel && (!this.channelDispatchers || !this.channelDispatchers[eventName])) {
            const debugHandler = (data) => {
                const handlers = this.eventHandlers[eventName];
                if (handlers) {
                    handlers.forEach((fn) => {
                        try {
                            fn(data);
                        } catch (err) {
                            console.error(`❌ Error in handler for '${eventName}':`, err);
                        }
                    });
                }
            };
            
            this.channelDispatchers = this.channelDispatchers || {};
            this.channelDispatchers[eventName] = debugHandler;
            this.channel.bind(eventName, debugHandler);
        }
        
        console.log('✅ Event handler bound successfully for:', eventName);

        return () => {
            this.removeEventHandler(eventName, handler);
        };
    }

    removeEventHandler(eventName, handler) {
        if (!this.channel || !this.eventHandlers[eventName]) return;
        
        if (handler) {
            this.eventHandlers[eventName].delete(handler);
        }

        if (!handler || this.eventHandlers[eventName].size === 0) {
            if (this.channelDispatchers?.[eventName]) {
                this.channel.unbind(eventName, this.channelDispatchers[eventName]);
                delete this.channelDispatchers[eventName];
            }
            delete this.eventHandlers[eventName];
        }
    }

    reconnect() {
        if (this.pusher) {
            this.pusher.connect();
        }
    }

    sendMessage(eventName, data) {
        if (this.channel && this.connectionStatus === 'connected') {
            this.channel.trigger(eventName, data);
            return true;
        }
        return false;
    }

    disconnect() {
        if (this.pusher) {
            // Unbind all events
            if (this.channelDispatchers && this.channel) {
                Object.entries(this.channelDispatchers).forEach(([eventName, handler]) => {
                    this.channel.unbind(eventName, handler);
                });
            }
            
            this.pusher.disconnect();
            this.isInitialized = false;
            this.eventHandlers = {};
            this.channelDispatchers = {};
        }
    }
}

// Create singleton instance
const pusherManager = new PusherManager();

/**
 * Table Pusher Hook - Real-time WebSocket connection for table updates
 * Uses a singleton Pusher instance that's initialized once per application lifecycle
 * 
 * @example Basic Usage:
 * ```jsx
 * import useTablePusher from './path/to/table-pusher';
 * 
 * const MyComponent = () => {
 *   const { 
 *     connectionStatus, 
 *     lastUpdate, 
 *     error, 
 *     addEventHandler,
 *     isConnected 
 *   } = useTablePusher();
 * 
 *   useEffect(() => {
 *     if (isConnected) {
 *       console.log('Connected to Pusher!');
 *     }
 *   }, [isConnected]);
 * 
 *   useEffect(() => {
 *     // Add custom event handler
 *     addEventHandler('custom-event', (data) => {
 *       console.log('Custom event:', data);
 *     });
 *   }, [addEventHandler]);
 * 
 *   return (
 *     <div>
 *       <p>Status: {connectionStatus}</p>
 *       {error && <p>Error: {error}</p>}
 *       {lastUpdate && (
 *         <p>Last Update: {JSON.stringify(lastUpdate)}</p>
 *       )}
 *     </div>
 *   );
 * };
 * ```
 */

const PusherHandler = () => {
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [lastUpdate, setLastUpdate] = useState(null);
    const [error, setError] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const initializationAttempted = useRef(false);
    
    // Store registered handlers for this hook instance to prevent duplicates/leaks
    const localHandlersRef = useRef({});

    // Initialize Pusher once when hook is first used
    useEffect(() => {
        if (!initializationAttempted.current) {
            initializationAttempted.current = true;
            
            pusherManager.initialize().catch(err => {
                console.error('Failed to initialize Pusher:', err);
            });
        }

        // Subscribe to pusher manager updates
        const unsubscribe = pusherManager.subscribe((type, data) => {
            if (type === 'status') {
                setConnectionStatus(data.connectionStatus);
                setError(data.error);
                setIsConnected(data.isConnected);
            } else if (type === 'update') {
                setLastUpdate(data);
            }
        });

        return () => {
            unsubscribe();
            // Automatically clean up all event handlers registered by this component hook instance when it unmounts
            Object.entries(localHandlersRef.current).forEach(([eventName, handler]) => {
                pusherManager.removeEventHandler(eventName, handler);
            });
        };
    }, []);

    // Method to add custom event handlers
    const addEventHandler = useCallback((eventName, handler) => {
        // If this hook instance already registered a handler for this eventName, remove the old one first
        if (localHandlersRef.current[eventName]) {
            pusherManager.removeEventHandler(eventName, localHandlersRef.current[eventName]);
        }
        localHandlersRef.current[eventName] = handler;

        return pusherManager.addEventHandler(eventName, handler);
    }, []);

    // Method to remove event handlers
    const removeEventHandler = useCallback((eventName, handler) => {
        const targetHandler = handler || localHandlersRef.current[eventName];
        if (targetHandler) {
            pusherManager.removeEventHandler(eventName, targetHandler);
            if (localHandlersRef.current[eventName] === targetHandler) {
                delete localHandlersRef.current[eventName];
            }
        }
    }, []);

    // Method to manually reconnect
    const reconnect = useCallback(() => {
        pusherManager.reconnect();
    }, []);

    // Method to send message
    const sendMessage = useCallback((eventName, data) => {
        return pusherManager.sendMessage(eventName, data);
    }, []);

    return {
        pusher: pusherManager.pusher,
        channel: pusherManager.channel,
        connectionStatus,
        lastUpdate,
        error,
        addEventHandler,
        removeEventHandler,
        reconnect,
        sendMessage,
        isConnected
    };
};

// Export the manager instance for advanced usage
export { pusherManager };

export default PusherHandler;